import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators'
import Apis from '@/Apis/_'
import { Consolidado, RegistroServico, SERVICOS, Servico, TipoBusca, avaliar, calcularScore, camposDivergentes, normalizarCpf, normalizarEmail } from '@/components/Suporte/diagnosticoUsuarioEngine'
import { ConviteUsuarioDiagnostico, ConviteUsuarioDiagnosticoPayload } from '@/types/interfaces/ConviteUsuarioDiagnostico'
import { UsuarioDiagnosticoAgendamento, UsuarioDiagnosticoAgendamentoPayload, UsuarioDiagnosticoRemuneracao, UsuarioDiagnosticoRemuneracaoPayload, UsuarioSegurancaPermissoes } from '@/types/interfaces/UsuarioDiagnostico'
import ResponseApi from '@/types/interfaces/FactoryResponse'

const SERVICOS_INDISPONIVEL = (s: Servico, erro?: string): RegistroServico => ({
  servico: s,
  status: 'indisponivel',
  erro,
})

// O Agendamento responde "não encontrado" como erro; só essa mensagem distingue do erro real (ex.: sem permissão)
const AGENDAMENTO_PESSOA_NAO_ENCONTRADA = 'Nenhuma pessoa encontrada.'

const SERVICO_NAO_ENCONTRADO = (s: Servico): RegistroServico => ({
  servico: s,
  status: 'nao_encontrado',
})

// Extrai a mensagem de um erro de forma tolerante (evita optional chaining,
// não suportado pelo toolchain de build do projeto).
const msgErro = (e: any): string | undefined => (e ? e.message : undefined)

// -------------------------------------------------------------------------
// Mapeadores: cada um adapta o payload do backend para RegistroServico.
// Precisam ser funções de módulo (e não métodos da classe): dentro de uma
// @Action o `this` do vuex-module-decorators é um proxy que só expõe state,
// getters, mutations e actions — métodos privados não existem nele, o que
// causava "this.mapearCadastro is not a function".
// -------------------------------------------------------------------------

function mapearCadastro(dado: any): RegistroServico {
  const d = dado || {}
  const docs = d.documentos || d.Documentos || []
  const contatos = d.contatos || d.Contatos || []
  const cpfDoc = (Array.isArray(docs) ? docs.find((x: any) => (x.tipo || x.Tipo) === 'CPF') : null) || {}
  const emailCont = (Array.isArray(contatos) ? contatos.find((x: any) => (x.tipoContato || x.TipoContato) === 'Email') : null) || {}
  const telefones = Array.isArray(contatos)
    ? contatos.filter((x: any) => (x.tipoContato || x.TipoContato) !== 'Email').map((x: any) => x.valor || x.Valor)
    : []
  return {
    servico: 'cadastro',
    status: 'encontrado',
    id: d.id || d.Id,
    userId: d.id || d.Id,
    cpf: cpfDoc.numero || cpfDoc.Numero || cpfDoc.valor || cpfDoc.Valor,
    nome: d.nome || d.Nome,
    email: emailCont.valor || emailCont.Valor,
    telefones,
    atualizadoEm: d.dataCadastro || d.DataCadastro,
    ativo: d.dataExclusao == null && d.DataExclusao == null,
  }
}

const simNao = (valor: boolean): string => (valor ? 'sim' : 'não')

function mapearAgendamento(d: UsuarioDiagnosticoAgendamento): RegistroServico {
  return {
    servico: 'agendamento',
    status: 'encontrado',
    id: d.id,
    userId: d.id,
    cpf: d.cpf || undefined,
    nome: d.nome || undefined,
    email: d.email || undefined,
    telefones: d.telefone ? [d.telefone] : [],
    crm: d.conselhoNumero || undefined,
    tipo: d.tipoUsuarioAgendamento || undefined,
    detalhes: [
      { label: 'Médico tem usuário', valor: simNao(d.medicoTemUsuario) },
      { label: 'Vínculo c/ solicitação', valor: simNao(d.temVinculoComSolicitacao) },
      { label: 'User hospital', valor: simNao(d.temUserHospital), alerta: !d.temUserHospital },
      { label: 'Usuário hospital', valor: simNao(d.temUsuarioHospital), alerta: !d.temUsuarioHospital },
    ],
  }
}

function mapearRemuneracao(d: UsuarioDiagnosticoRemuneracao): RegistroServico {
  const vinculoAtivo = d.vinculosGrupoHospital.find(v => v.ativo)
  const detalhes = [
    ...d.vinculosGrupoHospital.map(v => ({
      label: `Grupo ${v.grupoHospitalNome}`,
      valor: `${v.perfil} · ${v.emailUsuario} · ${v.ativo ? 'ativo' : 'inativo'}`,
      alerta: !v.ativo,
    })),
    ...d.configuracoes.map(c => ({
      label: `Config. ${c.hospitalNome}`,
      valor: c.emailNotificacao || 'sem e-mail de notificação',
      alerta: !c.possuiEmailNotificacao,
    })),
    ...d.logsLiberacao.map(l => ({
      label: `Liberação ${l.dataTentativa}`,
      valor: l.sucesso ? 'sucesso' : l.erroMensagem,
      alerta: !l.sucesso,
    })),
  ]

  return {
    servico: 'remuneracao',
    status: 'encontrado',
    id: d.userId || undefined,
    userId: d.userId || undefined,
    cpf: d.cpf || undefined,
    nome: d.nome || undefined,
    email: vinculoAtivo ? vinculoAtivo.emailUsuario : undefined,
    ativo: d.temVinculoGrupoHospital ? !!vinculoAtivo : undefined,
    detalhes,
  }
}

function mapearPermissoesSeguranca(p: UsuarioSegurancaPermissoes): Pick<RegistroServico, 'gruposPermissao' | 'detalhes'> {
  const perfis = p.roles || []
  const permissoesExtras = p.permissoesExtras || []
  // Mesma regra dos backends (ApiSegurancaService): permissões extras + permissões dos perfis
  const permissoesPerfis = perfis.reduce<string[]>((acc, perfil) => acc.concat(perfil.permissoes || []), [])
  const efetivas = Array.from(new Set([...permissoesExtras, ...permissoesPerfis])).sort()
  const defaultPage = p.defaultPage || {}

  return {
    gruposPermissao: [
      { titulo: 'Permissões efetivas', itens: efetivas },
      { titulo: 'Permissões extras', itens: permissoesExtras },
      ...perfis.map(perfil => ({ titulo: `Perfil ${perfil.nome}`, itens: perfil.permissoes || [] })),
      { titulo: 'Páginas autorizadas', itens: p.legacyPageAuthorized || [] },
      { titulo: 'Legacy roles', itens: p.legacyRoles || [] },
    ],
    detalhes: [
      { label: 'Perfil legado', valor: p.legacyProfile || '—' },
      { label: 'Página padrão', valor: defaultPage.view ? `${defaultPage.view} (${defaultPage.keyAuthorized || '—'})` : '—' },
    ],
  }
}

function mapearSeguranca(dado: any, permissoes: ResponseApi<UsuarioSegurancaPermissoes>): RegistroServico {
  const d = dado || {}
  const registro: RegistroServico = {
    servico: 'seguranca',
    status: 'encontrado',
    id: d.id,
    userId: d.id,
    email: d.login || d.email,
    ativo: typeof d.ativo === 'boolean' ? d.ativo : undefined,
  }

  if (!permissoes.success || !permissoes.data) {
    return {
      ...registro,
      detalhes: [{ label: 'Permissões', valor: `falha ao consultar (${permissoes.message || 'sem mensagem'})`, alerta: true }],
    }
  }

  return { ...registro, ...mapearPermissoesSeguranca(permissoes.data) }
}

function mapearKeycloak(dado: any): RegistroServico {
  const d = dado || {}
  const cpfAttr = d.attributes && d.attributes.cpf
  return {
    servico: 'keycloak',
    status: 'encontrado',
    id: d.id,
    userId: d.id,
    cpf: Array.isArray(cpfAttr) ? cpfAttr[0] : cpfAttr,
    nome: [d.firstName, d.lastName].filter(Boolean).join(' ').trim() || undefined,
    email: d.email || d.username,
    ativo: typeof d.enabled === 'boolean' ? d.enabled : undefined,
    criadoEm: d.createdTimestamp ? new Date(d.createdTimestamp).toISOString() : undefined,
    extras: { username: d.username, federationLink: d.federationLink },
  }
}

// Deduplica por (servico, id). Garante que cada serviço apareça pelo menos
// uma vez (com status nao_encontrado se não foi achado em lugar nenhum).
function dedup(regs: RegistroServico[]): RegistroServico[] {
  const seen = new Map<string, RegistroServico>()
  regs.forEach((r) => {
    const key = `${r.servico}|${(r.id || '').toLowerCase()}`
    if (!seen.has(key)) seen.set(key, r)
  })
  // garante presença mínima de cada serviço (status nao_encontrado)
  const presentes = new Set(Array.from(seen.values()).map(r => r.servico))
  SERVICOS.forEach((s) => {
    if (!presentes.has(s)) seen.set(`${s}|`, SERVICO_NAO_ENCONTRADO(s))
  })
  return Array.from(seen.values())
}

/**
 * Diagnóstico de Usuário (FXCS-4128).
 *
 * Orquestra a busca em cascata nos 5 serviços conforme o tipo de entrada:
 *  - CPF:    Fase 1 Cadastro+Agendamento+Remuneração (por CPF) → Fase 2 Segurança+Keycloak (por UserId/email descobertos)
 *  - ID:     Fase única paralela nos 5 serviços
 *  - EMAIL:  Fase 1 Segurança+Keycloak+Agendamento+Remuneração (por email) → Fase 2 Cadastro (por UserId descoberto)
 *
 * A spec detalhada está em spec-fxcs-4128-diagnostico-usuario.md §4.
 */
@Module({ namespaced: true, name: 'SuporteDiagnosticoUsuarioStore' })
export default class SuporteDiagnosticoUsuarioStore extends VuexModule {
  public tipoBusca: TipoBusca = 'cpf'
  public valorBusca: string = ''
  public carregando: boolean = false
  public consolidado: Consolidado | null = null
  public erroGeral: string = ''
  public convites: ConviteUsuarioDiagnostico[] | null = null
  public erroConvites: string = ''

  @Mutation
  setTipoBusca(value: TipoBusca) {
    this.tipoBusca = value
    this.valorBusca = ''
    this.consolidado = null
    this.erroGeral = ''
    this.convites = null
    this.erroConvites = ''
  }

  @Mutation
  setConvites({ convites, erro }: { convites: ConviteUsuarioDiagnostico[] | null; erro: string }) {
    this.convites = convites
    this.erroConvites = erro
  }

  @Mutation
  setValorBusca(value: string) {
    this.valorBusca = value
  }

  @Mutation
  setCarregando(value: boolean) {
    this.carregando = value
  }

  @Mutation
  setConsolidado(value: Consolidado | null) {
    this.consolidado = value
  }

  @Mutation
  setErroGeral(value: string) {
    this.erroGeral = value
  }

  @Mutation
  limpar() {
    this.valorBusca = ''
    this.consolidado = null
    this.erroGeral = ''
    this.convites = null
    this.erroConvites = ''
  }

  get camposDivergentes(): Set<string> {
    return this.consolidado ? camposDivergentes(this.consolidado.achados) : new Set()
  }

  // -------------------------------------------------------------------------
  // Buscas isoladas por serviço (cada uma é tolerante a falha)
  // -------------------------------------------------------------------------

  @Action
  async buscarCadastroPorCpf(cpf: string): Promise<RegistroServico[]> {
    try {
      const r = await Apis.Cadastro.CadastroApi.getPessoaByCpf(cpf)
      if (!r.success || !r.data) return [SERVICO_NAO_ENCONTRADO('cadastro')]
      return [mapearCadastro(r.data)]
    } catch (e) {
      return [SERVICOS_INDISPONIVEL('cadastro', msgErro(e))]
    }
  }

  @Action
  async buscarCadastroPorId(id: string): Promise<RegistroServico[]> {
    try {
      const r = await Apis.Cadastro.CadastroApi.getPessoaInfoById(id)
      if (!r.success || !r.data) return [SERVICO_NAO_ENCONTRADO('cadastro')]
      return [mapearCadastro(r.data)]
    } catch (e) {
      return [SERVICOS_INDISPONIVEL('cadastro', msgErro(e))]
    }
  }

  @Action
  async buscarAgendamento(payload: UsuarioDiagnosticoAgendamentoPayload): Promise<RegistroServico[]> {
    try {
      const r = await Apis.Agendamento.SuporteApi.getUsuarioDiagnostico(payload)
      if (r.success && r.data) return [mapearAgendamento(r.data)]
      if (r.message === AGENDAMENTO_PESSOA_NAO_ENCONTRADA) return [SERVICO_NAO_ENCONTRADO('agendamento')]
      return [SERVICOS_INDISPONIVEL('agendamento', r.message || 'Erro ao consultar o Agendamento.')]
    } catch (e) {
      return [SERVICOS_INDISPONIVEL('agendamento', msgErro(e))]
    }
  }

  @Action
  async buscarRemuneracao(payload: UsuarioDiagnosticoRemuneracaoPayload): Promise<RegistroServico[]> {
    try {
      const r = await Apis.RemuneracaoMedica.RemuneracaoMedicaApi.getUsuarioDiagnostico(payload)
      if (!r.success || !r.data) return [SERVICOS_INDISPONIVEL('remuneracao', r.message || 'Erro ao consultar a Remuneração.')]
      if (!r.data.pessoaEncontrada) return [SERVICO_NAO_ENCONTRADO('remuneracao')]
      return [mapearRemuneracao(r.data)]
    } catch (e) {
      return [SERVICOS_INDISPONIVEL('remuneracao', msgErro(e))]
    }
  }

  @Action
  async buscarSegurancaPorId(id: string): Promise<RegistroServico[]> {
    try {
      const [r, permissoes] = await Promise.all([
        Apis.Seguranca.SegurancaApi.getById(id),
        Apis.Seguranca.SegurancaApi.getPermissoesById(id),
      ])
      if (!r.success || !r.data) return [SERVICO_NAO_ENCONTRADO('seguranca')]
      return [mapearSeguranca(r.data, permissoes)]
    } catch (e) {
      return [SERVICOS_INDISPONIVEL('seguranca', msgErro(e))]
    }
  }

  @Action
  async buscarSegurancaPorEmail(email: string): Promise<RegistroServico[]> {
    try {
      const r = await Apis.Seguranca.SegurancaApi.getByLogin(email)
      if (!r.success || !r.data) return [SERVICO_NAO_ENCONTRADO('seguranca')]
      const permissoes = await Apis.Seguranca.SegurancaApi.getPermissoesById(r.data.id)
      return [mapearSeguranca(r.data, permissoes)]
    } catch (e) {
      return [SERVICOS_INDISPONIVEL('seguranca', msgErro(e))]
    }
  }

  @Action
  async buscarKeycloak(params: { id?: string; email?: string }): Promise<RegistroServico[]> {
    try {
      const r = await Apis.Seguranca.SegurancaApi.getDiagnosticoKeycloak(params)
      if (!r.success) return [SERVICOS_INDISPONIVEL('keycloak', r.message || 'Erro ao consultar o Keycloak.')]
      const usuarios: any[] = (r.data && r.data.usuarios) || []
      if (usuarios.length === 0) return [SERVICO_NAO_ENCONTRADO('keycloak')]
      return usuarios.map(u => mapearKeycloak(u))
    } catch (e) {
      return [SERVICOS_INDISPONIVEL('keycloak', msgErro(e))]
    }
  }

  // Convites só são pesquisáveis por CPF ou e-mail (o ID da busca é de usuário, não de convite)
  @Action
  async buscarConvites(valor: string) {
    const payloadPorTipo: Partial<Record<TipoBusca, ConviteUsuarioDiagnosticoPayload>> = {
      cpf: { cpf: normalizarCpf(valor) },
      email: { email: normalizarEmail(valor) },
    }
    const payload = payloadPorTipo[this.tipoBusca]

    if (!payload) {
      return
    }

    try {
      const r = await Apis.Cadastro.CadastroApi.getConvitesDiagnostico(payload)

      if (!r.success) {
        this.context.commit('setConvites', { convites: null, erro: r.message || 'Erro ao consultar os convites.' })
        return
      }

      this.context.commit('setConvites', { convites: r.data || [], erro: '' })
    } catch (e) {
      this.context.commit('setConvites', { convites: null, erro: msgErro(e) || 'Erro ao consultar os convites.' })
    }
  }

  // -------------------------------------------------------------------------
  // Cascata principal
  // -------------------------------------------------------------------------

  @Action
  async executarBusca() {
    const valor = this.valorBusca.trim()
    if (!valor) return

    this.context.commit('setCarregando', true)
    this.context.commit('setErroGeral', '')
    this.context.commit('setConsolidado', null)
    this.context.commit('setConvites', { convites: null, erro: '' })

    const inicio = Date.now()
    let registros: RegistroServico[] = []
    const buscaConvites = this.context.dispatch('buscarConvites', valor)

    try {
      if (this.tipoBusca === 'cpf') {
        registros = await this.context.dispatch('cascataPorCpf', valor)
      } else if (this.tipoBusca === 'id') {
        registros = await this.context.dispatch('cascataPorId', valor)
      } else if (this.tipoBusca === 'email') {
        registros = await this.context.dispatch('cascataPorEmail', valor)
      }
      await buscaConvites
    } catch (e) {
      this.context.commit('setErroGeral', msgErro(e) || 'Erro inesperado na busca.')
    }

    const consolidado: Consolidado = {
      chaveBusca: valor,
      tipoBusca: this.tipoBusca,
      registros,
      achados: [],
      score: 0,
      duracaoMs: Date.now() - inicio,
    }
    consolidado.achados = avaliar(consolidado)
    consolidado.score = calcularScore(consolidado.achados)

    this.context.commit('setConsolidado', consolidado)
    this.context.commit('setCarregando', false)
  }

  @Action
  async cascataPorCpf(cpf: string): Promise<RegistroServico[]> {
    const cpfNorm = normalizarCpf(cpf)
    // Fase 1: Cadastro + Agendamento + Remuneração (têm CPF)
    const [cadastros, agendamentos, remuneracoes] = await Promise.all([
      this.context.dispatch('buscarCadastroPorCpf', cpfNorm),
      this.context.dispatch('buscarAgendamento', { cpf: cpfNorm }),
      this.context.dispatch('buscarRemuneracao', { cpf: cpfNorm }),
    ])
    // Coleta UserId/email descobertos
    const userIds = new Set<string>()
    const emails = new Set<string>()
    const achadosFase1 = [...cadastros, ...agendamentos, ...remuneracoes] as RegistroServico[]
    achadosFase1.forEach((r) => {
      if (r.status !== 'encontrado') return
      if (r.userId) userIds.add(r.userId.toLowerCase())
      else if (r.id) userIds.add(r.id.toLowerCase())
      if (r.email) emails.add(r.email.toLowerCase())
    })
    // Fase 2: Segurança + Keycloak para cada (UserId, email) descoberto
    const segurancas: RegistroServico[] = []
    const keycloaks: RegistroServico[] = []
    if (userIds.size === 0 && emails.size === 0) {
      segurancas.push(SERVICO_NAO_ENCONTRADO('seguranca'))
      keycloaks.push(SERVICO_NAO_ENCONTRADO('keycloak'))
    } else {
      const promessasSeg: Promise<RegistroServico[]>[] = []
      const promessasKc: Promise<RegistroServico[]>[] = []
      userIds.forEach((id) => {
        promessasSeg.push(this.context.dispatch('buscarSegurancaPorId', id) as Promise<RegistroServico[]>)
        promessasKc.push(this.context.dispatch('buscarKeycloak', { id }) as Promise<RegistroServico[]>)
      })
      emails.forEach((email) => {
        promessasSeg.push(this.context.dispatch('buscarSegurancaPorEmail', email) as Promise<RegistroServico[]>)
        promessasKc.push(this.context.dispatch('buscarKeycloak', { email }) as Promise<RegistroServico[]>)
      })
      const resSeg = await Promise.all(promessasSeg)
      const resKc = await Promise.all(promessasKc)
      resSeg.forEach(rs => segurancas.push(...rs))
      resKc.forEach(rs => keycloaks.push(...rs))
    }
    return dedup([...cadastros, ...agendamentos, ...remuneracoes, ...segurancas, ...keycloaks])
  }

  @Action
  async cascataPorId(id: string): Promise<RegistroServico[]> {
    const resultados = await Promise.all([
      this.context.dispatch('buscarCadastroPorId', id),
      this.context.dispatch('buscarAgendamento', { userId: id }),
      this.context.dispatch('buscarRemuneracao', { usuarioBuscaId: id }),
      this.context.dispatch('buscarSegurancaPorId', id),
      this.context.dispatch('buscarKeycloak', { id }),
    ])
    return dedup(resultados.flat())
  }

  @Action
  async cascataPorEmail(email: string): Promise<RegistroServico[]> {
    const emailNorm = normalizarEmail(email)
    // Fase 1: todos que aceitam e-mail
    const [segurancas, keycloaks, agendamentos, remuneracoes] = await Promise.all([
      this.context.dispatch('buscarSegurancaPorEmail', emailNorm),
      this.context.dispatch('buscarKeycloak', { email: emailNorm }),
      this.context.dispatch('buscarAgendamento', { email: emailNorm }),
      this.context.dispatch('buscarRemuneracao', { email: emailNorm }),
    ])
    // Coleta UserIds descobertos
    const userIds = new Set<string>()
    const achadosFase1 = [...segurancas, ...keycloaks, ...agendamentos, ...remuneracoes] as RegistroServico[]
    achadosFase1.forEach((r) => {
      if (r.status !== 'encontrado') return
      if (r.userId) userIds.add(r.userId.toLowerCase())
      else if (r.id) userIds.add(r.id.toLowerCase())
    })
    // Fase 2: Cadastro por id (não busca por e-mail)
    const cadastros: RegistroServico[] = []
    if (userIds.size === 0) {
      cadastros.push(SERVICO_NAO_ENCONTRADO('cadastro'))
    } else {
      const resCad = await Promise.all(
        Array.from(userIds).map(id => this.context.dispatch('buscarCadastroPorId', id) as Promise<RegistroServico[]>),
      )
      resCad.forEach(rs => cadastros.push(...rs))
    }
    return dedup([...cadastros, ...agendamentos, ...remuneracoes, ...segurancas, ...keycloaks])
  }
}
